import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { Layout, Spin, Tooltip } from "antd";
import "leaflet/dist/leaflet.css";
import * as turf from "@turf/turf";
// import logo from "../assets/Logo T&G.png"; // not used when header is hidden
import chroma from "chroma-js";
import { Grid } from "antd";
import Papa from "papaparse";
import MapComponent from "./Mappa";
import SiderComponent from "./Sider"; // reused as popup card
// Logo imports removed - footer simplified
import Traffix from "../assets/Traffixcolored.svg";
import { getClientType } from "../services/clientConfig";
import { fetchClientBlobUrl } from "../services/clientDataService";
import "../styles/GeoJson.css";
import {
  fetchMetricsData,
  fetchNetworkOffsetData,
  fetchTrafficCSV,
  fetchTrafficZoneData,
  fetchMapGeoJSON,
  fetchArchiZonaGeoJSON,
} from "../APIs/apiService";
import { useLoading } from "../contexts/LoadingContext";
import { useAuth } from "../contexts/AuthContext";
import { resolveMysteryClientCity } from "../services/mysteryClientService";
import { ScreensaverOverlay } from "../components";
import McClientLogo from "../components/McClientLogo";

const { useBreakpoint } = Grid;

const { Content } = Layout;

function GeoJson() {
  const { showLoader, hideLoader } = useLoading();
  const [geoData, setGeoData] = useState(null);
  const [dbData, setDbData] = useState(null);
  const [selectedHash, setSelectedHash] = useState([]);
  const [highlightedHash, setHighlightedHash] = useState(null);
  const [selectedFeature, setSelectedFeature] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  // const [collapsed, setCollapsed] = useState(false);
  const [siderSection, setSiderSection] = useState();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [speedDomain, setSpeedDomain] = useState([0, 1]);
  const [colorScale, setColorScale] = useState(() =>
    chroma.scale(["red", "yellow", "green"]).domain([0, 1])
  );
  const [showDeckGL, setShowDeckGL] = useState(false);
  const [mapMode, setMapMode] = useState("mappa"); // 'mappa', 'genova', 'nuvola'
  const [footerOpen, setFooterOpen] = useState(true);
  const [radarSelected, setRadarSelected] = useState(false);
  const [isLoadingGeoData, setIsLoadingGeoData] = useState(true);
  const [isMapCentered, setIsMapCentered] = useState(false);
  const hasFetchedDbData = useRef(false);

  // ── Mystery Client state ──────────────────────────────────────────────────
  const { hasService, allowedServices, allowedPermissions } = useAuth();
  const hasMysteryClient = hasService('mystery_client');
  const hasFcdNetwork = hasService('fcd_network');
  // When MC is available but FCD NETX is not, the main map should open on the client's city.
  // When FCD NETX is present, mystery-client map behaviour is ignored entirely.
  const shouldCenterOnMcCity = hasMysteryClient && !hasFcdNetwork;
  // True when the only module the user can open is mystery_client
  // (ignore unrelated permission strings that would break a naive size===1 check)
  const MODULE_KEYS = [
    'fcd_network', 'simfix_areas', 'predict', 'tl_green_wave',
    'signalfix', 'od_analysis', 'assignx', 'mystery_client',
  ];
  const hasOnlyMysteryClient = useMemo(() => {
    const enabled = MODULE_KEYS.filter((key) => {
      const services = Array.isArray(allowedServices) ? allowedServices : [];
      const permissions = Array.isArray(allowedPermissions) ? allowedPermissions : [];
      return services.includes(key) || permissions.includes(key);
    });
    return enabled.length === 1 && enabled[0] === 'mystery_client';
  }, [allowedServices, allowedPermissions]);

  const [mcCityData, setMcCityData] = useState(null); // { cityName, center, zoneGeoJSON, allCities }
  const hasFetchedMcData = useRef(false);

  // ── Mystery Client data fetch — only when FCD NETX is NOT available ───────
  useEffect(() => {
    if (!shouldCenterOnMcCity || hasFetchedMcData.current) return;
    hasFetchedMcData.current = true;
    const company = localStorage.getItem('company') || 'ATB';
    console.log('[MC] Fetching mystery client city for company:', company);
    showLoader('Caricamento mappa...', 1);
    resolveMysteryClientCity(company)
      .then((data) => {
        if (data?.center) {
          console.log('[MC] Resolved city:', data.cityName, data.center);
          setMcCityData(data);
          // Loader stays until map reports isMapCentered
        } else {
          console.warn('[MC] No city center resolved — dismissing loader');
          hideLoader();
        }
      })
      .catch((err) => {
        console.warn('[MC] Could not resolve mystery client city:', err);
        hideLoader();
      });
  }, [shouldCenterOnMcCity, showLoader, hideLoader]);

  // Dismiss loader only after the map has centered on the MC city
  useEffect(() => {
    if (shouldCenterOnMcCity && isMapCentered) {
      hideLoader();
    }
  }, [shouldCenterOnMcCity, isMapCentered, hideLoader]);

  // Network view mode state (when no road is selected)
  const [networkViewCategory, setNetworkViewCategory] = useState('velocita'); // 'velocita', 'flow', 'traveltime' — default speed to match map colors + legend
  const [networkViewDate, setNetworkViewDate] = useState(null);
  const [networkViewTime, setNetworkViewTime] = useState(null);
  const [availableNetworkDates, setAvailableNetworkDates] = useState([]);
  const [availableNetworkTimes, setAvailableNetworkTimes] = useState([]);

  // Default Traffix / client_type logo — used when FCD NETX is available
  const [clientLogo, setClientLogo] = useState(Traffix);
  useEffect(() => {
    if (!hasFcdNetwork) return;
    const clientType = getClientType();
    fetchClientBlobUrl(clientType, 'logo.png')
      .then(blobUrl => setClientLogo(blobUrl))
      .catch(() => setClientLogo(Traffix));
  }, [hasFcdNetwork]);

  // AI filter state - when set, only show these roads on the map
  const [aiFilteredRoads, setAiFilteredRoads] = useState(null);
  const mapInstanceRef = useRef(null);

  const handleMapCentered = useCallback(() => {
    setIsMapCentered(true);
  }, []);

  const handleZoomToRoad = useCallback((features) => {
    if (!mapInstanceRef.current || !features || !features.length) return;
    try {
      const L = window.L;
      if (!L) return;
      const bounds = L.geoJSON(features).getBounds();
      if (bounds.isValid()) {
        mapInstanceRef.current.fitBounds(bounds, { padding: [100, 100], maxZoom: 19 });
      }
    } catch (e) {
      console.error("Zoom failed:", e);
    }
  }, []);

  // Responsive footer height and handle size based on viewport/breakpoints
  const FOOTER_HEIGHT = screens.xl
    ? 40
    : screens.lg
      ? 40
      : screens.md
        ? 40
        : 40;
  // handle height no longer used (floating toggle replaces it)

  useEffect(() => {
    // Prevent multiple fetches
    if (hasFetchedDbData.current) return;
    hasFetchedDbData.current = true;

    // Without FCD NETX the map stays empty and only centers on the MC city —
    // skip FCD geo/metrics so loading is driven solely by city centering.
    if (shouldCenterOnMcCity) {
      setIsLoadingGeoData(false);
      return;
    }

    console.log("GeoJSON: Starting parallel data fetch (Metrics + GeoJSON)...");
    showLoader('Caricamento dati e mappa...', 2);
    setIsLoadingGeoData(true);

    let loadedMetrics = null;
    let loadedGeo = null;

    const tryEnrich = (metrics, geo) => {
      if (metrics && geo) {
        console.log("GeoJSON: Both datasets ready, performing enrichment...");
        try {
          const dataByHash = {};
          metrics.forEach((d) => {
            if (!dataByHash[d.UnifiedID]) {
              dataByHash[d.UnifiedID] = [];
            }
            dataByHash[d.UnifiedID].push(d);
          });

          const enrichedFeatures = geo.features.map((f) => {
            const lookupKey = f.properties.UnifiedID || f.properties.hash;
            const allRecords = dataByHash[lookupKey] || [];
            return {
              ...f,
              properties: {
                ...f.properties,
                allRecords: allRecords,
              },
            };
          });

          setGeoData({ ...geo, features: enrichedFeatures });
          console.log("GeoJSON: Enrichment complete");
        } catch (err) {
          console.error("GeoJSON enrichment error:", err);
        }
      }
    };

    // Task 1: Fetch GeoJSON data (Prioritized)
    fetchNetworkOffsetData()
      .catch(error => {
        console.warn("Primary geo fetch failed, trying fallbacks...", error);
        return fetchMapGeoJSON();
      })
      .catch(() => fetchArchiZonaGeoJSON())
      .then(geoJsonData => {
        console.log("GeoJSON: Geo data fetched, displaying map...");
        try {
          const js = offsetSegmentiNegativi(geoJsonData, 4);
          loadedGeo = js;

          // Display the map immediately without waiting for metrics
          setGeoData(js);
          setIsLoadingGeoData(false);
          hideLoader();

          // Try to enrich if metrics happened to finish first
          tryEnrich(loadedMetrics, loadedGeo);
        } catch (err) {
          console.error("GeoJSON processing error:", err);
          setIsLoadingGeoData(false);
          hideLoader();
        }
      })
      .catch(err => {
        console.error("Geo data fetch failed:", err);
        setIsLoadingGeoData(false);
        hideLoader();
      });

    // Task 2: Fetch and transform metrics data (Background)
    fetchMetricsData()
      .catch(error => {
        console.warn("Primary metrics fetch failed, trying fallbacks...", error);
        return fetchTrafficCSV();
      })
      .then(data => {
        // Fallback if csv fetch returned text
        if (typeof data === 'string') {
          return new Promise((resolve, reject) => {
            Papa.parse(data, {
              header: true,
              dynamicTyping: true,
              skipEmptyLines: true,
              complete: (results) => resolve(results.data),
              error: (err) => reject(err)
            });
          });
        }
        return data;
      })
      .catch(() => fetchTrafficZoneData())
      .then(data => {
        // Transform metrics data to compatible format
        const transformedData = data.map(item => {
          let formattedDate = '';
          let timestamp = 0;
          let hourValue = 0;

          if (item.t_start && typeof item.t_start === 'string' && item.t_start.includes(' ')) {
            const parts = item.t_start.split(' ');
            if (parts.length >= 2) {
              const datePart = parts[0];
              const timePart = parts[1];
              if (datePart.match(/^\d{4}-\d{2}-\d{2}$/)) {
                formattedDate = datePart;
                const timeComponents = timePart.split(':');
                hourValue = parseInt(timeComponents[0]) || 0;
                const dateObj = new Date(item.t_start.replace(' ', 'T'));
                timestamp = Math.floor(dateObj.getTime() / 1000);
              }
            }
          } else if (item.day_group_label) {
            const dayGroupParts = item.day_group_label.split('_')[0];
            const newFormatMatch = dayGroupParts.match(/^(\d{4})-(\d{2})-(\d{2})$/);
            const oldFormatMatch = dayGroupParts.match(/(\d+),?(\d+),?(\d+)-(\d+)-(\d+)/);
            if (newFormatMatch) {
              formattedDate = dayGroupParts;
              const [hour, minute] = (item.t_start || '00:00').split(':');
              hourValue = parseInt(hour) || 0;
              const dateObj = new Date(`${formattedDate}T${hour}:${minute}:00`);
              timestamp = Math.floor(dateObj.getTime() / 1000);
            } else if (oldFormatMatch) {
              const day = oldFormatMatch[3].padStart(2, '0');
              const month = oldFormatMatch[4].padStart(2, '0');
              const year = oldFormatMatch[5];
              formattedDate = `${year}-${month}-${day}`;
              const [hour, minute] = (item.t_start || '00:00').split(':');
              hourValue = parseInt(hour) || 0;
              const dateObj = new Date(`${formattedDate}T${hour}:${minute}:00`);
              timestamp = Math.floor(dateObj.getTime() / 1000);
            }
          }

          return {
            ...item,
            hash: item.UnifiedID,
            date: formattedDate ? formattedDate + 'T00:00:00.000' : '',
            timestamp: timestamp,
            time: timestamp,
            hour: item.hour !== undefined ? parseInt(item.hour) : hourValue,
            speed: item.AvgSp,
            travel_time_in_seconds: item.AvgTt,
            length_in_meters: item.len_sum_m,
          };
        });

        console.log("GeoJSON: Metrics loaded and transformed");
        loadedMetrics = transformedData;
        setDbData(transformedData);
        tryEnrich(loadedMetrics, loadedGeo);
      })
      .catch(err => console.error("Metrics fetch failed:", err));

  }, [shouldCenterOnMcCity, showLoader, hideLoader]);



  // Extract available dates and times for network view from CSV/metrics data
  useEffect(() => {
    if (!dbData || dbData.length === 0) return;

    const dates = new Set();
    const times = new Set();

    dbData.forEach((d) => {
      // Priority 1: Extract from t_start field (format: "2024-08-01 00:00:00")
      if (d.t_start && typeof d.t_start === 'string' && d.t_start.trim() !== '') {
        const parts = d.t_start.split(' ');
        if (parts.length >= 2) {
          // Date is the first part: "2024-08-01"
          const datePart = parts[0];
          if (datePart.match(/^\d{4}-\d{2}-\d{2}$/)) {
            dates.add(datePart);
          }
          // Time is the second part: "00:00:00" -> extract "00:00"
          const timePart = parts[1];
          if (timePart) {
            const timeShort = timePart.substring(0, 5); // "00:00"
            times.add(timeShort);
          }
        }
      }
      // Priority 2: Extract from day_type + hour fields (saronno format)
      else if (d.day_type && d.hour !== undefined && d.hour !== null && d.hour !== '') {
        // Use day_type as the date key (e.g., "festivo", "sabato", "feriale")
        dates.add(d.day_type);
        // Use hour as time (convert to "HH:00" format)
        const hourNum = parseInt(d.hour, 10);
        if (!isNaN(hourNum)) {
          const timeStr = String(hourNum).padStart(2, '0') + ':00';
          times.add(timeStr);
        }
      }
      // Priority 3: Extract dates from day_group_label field (if it contains date pattern)
      else if (d.day_group_label) {
        const dayGroupParts = d.day_group_label.split('_')[0];

        // Check if it's date format (YYYY-MM-DD)
        const newFormatMatch = dayGroupParts.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (newFormatMatch) {
          dates.add(dayGroupParts);
        } else {
          // Old format: Split by hyphen to separate days from month-year
          const parts = dayGroupParts.split('-');
          if (parts.length === 3) {
            const daysStr = parts[0];
            const month = parts[1].padStart(2, '0');
            const year = parts[2];

            const days = daysStr.split(',');
            days.forEach(day => {
              if (day && day.trim()) {
                const dayPadded = day.trim().padStart(2, '0');
                dates.add(`${year}-${month}-${dayPadded}`);
              }
            });
          } else {
            // Text-based day_group_label without hour - use as-is
            dates.add(d.day_group_label);
          }
        }
      }
      // Priority 4: Fallback to timestamp_utc
      else if (d.timestamp_utc) {
        const [date, time] = d.timestamp_utc.split(" ");
        dates.add(date);
        times.add(time.split("+")[0]);
      }
      // Priority 5: Fallback to date field
      else if (d.date) {
        dates.add(d.date.split("T")[0]);
      }
    });

    const sortedDates = Array.from(dates).sort();
    const sortedTimes = Array.from(times).sort();

    setAvailableNetworkDates(sortedDates);
    setAvailableNetworkTimes(sortedTimes);

    // Set initial date and time if available - try to use 01/08/2024 as default
    if (sortedDates.length > 0 && !networkViewDate) {
      const defaultDate = "2024-08-01";
      if (sortedDates.includes(defaultDate)) {
        setNetworkViewDate(defaultDate);
      } else {
        setNetworkViewDate(sortedDates[0]);
      }
    }
    if (sortedTimes.length > 0 && !networkViewTime) {
      setNetworkViewTime(sortedTimes[0]);
    }
  }, [dbData, networkViewDate, networkViewTime]);

  // Listen for highlightRoad event from markers
  useEffect(() => {
    const handleHighlightRoad = (event) => {
      const { hash } = event.detail;
      setHighlightedHash(hash);
    };

    const handleResetHighlight = () => {
      setHighlightedHash(null);
    };

    window.addEventListener('highlightRoad', handleHighlightRoad);
    window.addEventListener('resetHighlight', handleResetHighlight);

    return () => {
      window.removeEventListener('highlightRoad', handleHighlightRoad);
      window.removeEventListener('resetHighlight', handleResetHighlight);
    };
  }, []);

  // Effect to load GeoJSON removed - integrated into parallel mount effect


  // Separate effect to recalculate colors based on network view settings
  useEffect(() => {
    if (!geoData || !geoData.features || geoData.features.length === 0) return;

    console.log("Recalculating color scale...");

    // Get the current values for coloring based on network view settings
    const values = geoData.features.map((f) => {
      const allRecords = f.properties.allRecords || [];

      // Find the record for the selected date/time if in network view
      let rec;
      if (
        networkViewCategory &&
        networkViewDate &&
        networkViewTime &&
        selectedHash.length === 0
      ) {
        // Match by date AND time
        rec = allRecords.find((d) => {
          // Priority 1: Check t_start field (format: "2024-08-01 00:00:00")
          if (d.t_start && typeof d.t_start === 'string' && d.t_start.trim() !== '' && d.t_start.includes(' ')) {
            const parts = d.t_start.split(' ');
            if (parts.length >= 2) {
              const datePart = parts[0]; // "2024-08-01"
              const timePart = parts[1]; // "00:00:00"
              const timeShort = timePart.substring(0, 5); // "00:00"

              if (datePart === networkViewDate && timeShort === networkViewTime) {
                return true;
              }
            }
            return false;
          }

          // Priority 2: Check day_type + hour fields (saronno format)
          if (d.day_type && d.hour !== undefined && d.hour !== null && d.hour !== '') {
            // Match day_type as the date key (e.g., "festivo", "sabato", "feriale")
            if (d.day_type === networkViewDate) {
              // Match hour as time (convert to "HH:00" format)
              const hourNum = parseInt(d.hour, 10);
              if (!isNaN(hourNum)) {
                const recordTime = String(hourNum).padStart(2, '0') + ':00';
                if (recordTime === networkViewTime) {
                  return true;
                }
              }
            }
            return false;
          }

          // Priority 3: Check day_group_label field with date patterns
          if (d.day_group_label) {
            const dayGroupParts = d.day_group_label.split('_')[0];

            // Check if it's new format (YYYY-MM-DD)
            const newFormatMatch = dayGroupParts.match(/^(\d{4})-(\d{2})-(\d{2})$/);
            if (newFormatMatch) {
              // New format: direct comparison
              if (dayGroupParts === networkViewDate) {
                const recordTime = (d.ora || '00:00').substring(0, 5);
                if (recordTime === networkViewTime) {
                  return true;
                }
              }
            } else {
              // Old format: parse DD,DD,DD-MM-YYYY
              const parts = dayGroupParts.split('-');
              if (parts.length === 3) {
                const daysStr = parts[0];
                const month = parts[1].padStart(2, '0');
                const year = parts[2];

                const days = daysStr.split(',');
                for (const day of days) {
                  if (day && day.trim()) {
                    const dayPadded = day.trim().padStart(2, '0');
                    const recordDate = `${year}-${month}-${dayPadded}`;
                    if (recordDate === networkViewDate) {
                      // Also check time match
                      const recordTime = (d.ora || '00:00').substring(0, 5);
                      if (recordTime === networkViewTime) {
                        return true;
                      }
                    }
                  }
                }
              }
            }
          }
          return false;
        }) || allRecords[0] || {};
      } else {
        // Use first available record as default
        rec = allRecords[0] || {};
      }

      // Get the value based on category using metrics.json fields
      if (networkViewCategory === "velocita") {
        return parseFloat(rec.AvgSp) || parseFloat(rec.speed) || 0;
      } else if (networkViewCategory === "flow") {
        // Use exactly Flusso_Espanso
        const value = parseFloat(rec.Flusso_Espanso) || parseFloat(rec.flow) || 0;
        return value;
      } else if (networkViewCategory === "traveltime") {
        // Use Travel Time field from metrics.json
        return parseFloat(rec.AvgTt) || parseFloat(rec.travel_time_in_seconds) || 0;
      } else {
        // Default to speed
        return parseFloat(rec.AvgSp) || parseFloat(rec.speed) || 0;
      }
    });

    // Calculate min and max for color scale
    let minS, maxS, colorArray;
    const YlGnBu = [
      "#081d58", "#253494", "#225ea8", "#1d91c0", "#41b6c4",
      "#7fcdbb", "#a1dab4", "#c7e9b4", "#edf8b1", "#ffffd9"
    ];

    if (networkViewCategory === "velocita") {
      minS = 0;
      maxS = 130;
      colorArray = ["#FF0000", "#FFFF00", "#A1E340"]; // Red (0) -> Yellow -> Green (130)
    } else if (networkViewCategory === "traveltime") {
      const validValues = values.filter((v) => v > 0);
      if (validValues.length > 0) {
        minS = Math.min(...validValues);
        maxS = Math.max(...validValues);
        if (minS === maxS) {
          minS = minS * 0.8;
          maxS = maxS * 1.2;
        }
      } else {
        minS = 0;
        maxS = 300; // Default range for travel time
      }
      colorArray = ["#A1E430", "#FFFF00", "#FF0000"]; // Green (short) to Red (long)
    } else if (networkViewCategory === "flow") {
      const validValues = values.filter((v) => v > 0);
      if (validValues.length > 0) {
        minS = Math.min(...validValues);
        maxS = Math.max(...validValues);
        if (minS === maxS) {
          minS = minS * 0.9;
          maxS = maxS * 1.1;
        }
      } else {
        minS = 0;
        maxS = 1000; // Default flow range
      }
      // Use Green-Yellow-Red scale to match the style of Velocita
      colorArray = ["#A1E340", "#FFFF00", "#FF0000"]; // Green (low) to Red (high)
    } else {
      // Default (velocita)
      minS = 0;
      maxS = 130;
      colorArray = ["#FF0000", "#FFFF00", "#A1E340"];
    }

    const scale = chroma
      .scale(colorArray)
      .domain([minS, maxS]); // Reverted to continuous gradient as requested

    setSpeedDomain([minS, maxS]);
    setColorScale(() => scale);

    console.log(`Color scale updated: ${minS.toFixed(2)} - ${maxS.toFixed(2)}`);
  }, [
    geoData,
    networkViewCategory,
    networkViewDate,
    networkViewTime,
    selectedHash.length,
  ]);

  const styleFeature = (feature) => {
    // Use UnifiedID or hash for checking selection
    const featureHash = feature.properties.hash || feature.properties.UnifiedID;
    const isSel = selectedHash.includes(featureHash);
    // Only highlight if highlightedHash is set AND matches the street name
    const isHighlighted = highlightedHash && highlightedHash === feature.properties.StreetName;

    // Get the appropriate record based on network view settings
    const allRecords = feature.properties.allRecords || [];
    let rec;

    if (
      networkViewCategory &&
      networkViewDate &&
      networkViewTime &&
      selectedHash.length === 0
    ) {
      // In network view mode, filter by selected date/time
      rec = allRecords.find((d) => {
        // Priority 1: Check t_start field (format: "2024-08-01 00:00:00")
        if (d.t_start && typeof d.t_start === 'string' && d.t_start.trim() !== '' && d.t_start.includes(' ')) {
          const parts = d.t_start.split(' ');
          if (parts.length >= 2) {
            const datePart = parts[0]; // "2024-08-01"
            const timePart = parts[1]; // "00:00:00"
            const timeShort = timePart.substring(0, 5); // "00:00"

            if (datePart === networkViewDate && timeShort === networkViewTime) {
              return true;
            }
          }
          return false;
        }

        // Priority 2: Check day_type + hour fields (saronno format)
        if (d.day_type && d.hour !== undefined && d.hour !== null && d.hour !== '') {
          // Match day_type as the date key (e.g., "festivo", "sabato", "feriale")
          if (d.day_type === networkViewDate) {
            // Match hour as time (convert to "HH:00" format)
            const hourNum = parseInt(d.hour, 10);
            if (!isNaN(hourNum)) {
              const recordTime = String(hourNum).padStart(2, '0') + ':00';
              if (recordTime === networkViewTime) {
                return true;
              }
            }
          }
          return false;
        }

        // Priority 3: Match by date from day_group_label with date patterns
        if (d.day_group_label) {
          const dayGroupParts = d.day_group_label.split('_')[0];

          // Check if it's new format (YYYY-MM-DD)
          const newFormatMatch = dayGroupParts.match(/^(\d{4})-(\d{2})-(\d{2})$/);
          if (newFormatMatch) {
            // New format: direct comparison
            if (dayGroupParts === networkViewDate) {
              const recordTime = (d.ora || '00:00').substring(0, 5);
              if (recordTime === networkViewTime) {
                return true;
              }
            }
          } else {
            // Old format: parse DD,DD,DD-MM-YYYY
            const parts = dayGroupParts.split('-');
            if (parts.length === 3) {
              const daysStr = parts[0];
              const month = parts[1].padStart(2, '0');
              const year = parts[2];

              const days = daysStr.split(',');
              for (const day of days) {
                if (day && day.trim()) {
                  const dayPadded = day.trim().padStart(2, '0');
                  const recordDate = `${year}-${month}-${dayPadded}`;
                  if (recordDate === networkViewDate) {
                    // Also check time match
                    const recordTime = (d.ora || '00:00').substring(0, 5);
                    if (recordTime === networkViewTime) {
                      return true;
                    }
                  }
                }
              }
            }
          }
        }
        return false;
      }) || allRecords[0] || {};

      // DEBUG: Verify if a record was found for the current time
      if (!rec.t_start && !rec.hour && !rec.ora && allRecords.length > 0) {
        rec = allRecords[0];
      }
    } else {
      rec = allRecords[0] || {};
    }

    // Determine which value to use for coloring based on metrics.json fields
    let value;
    let hasData = false;

    if (networkViewCategory) {
      if (networkViewCategory === "velocita") {
        // Use AvgSp field from metrics.json
        value = parseFloat(rec.AvgSp) || parseFloat(rec.speed) || 0;
        hasData = value > 0;
      } else if (networkViewCategory === "flow") {
        // Use Flusso_Espanso as flow indicator (VEH)
        value = parseFloat(rec.Flusso_Espanso) || parseFloat(rec.flow) || 0;
        hasData = value >= 0 && rec.Flusso_Espanso !== undefined;
      } else if (networkViewCategory === "traveltime") {
        // Use Travel Time for coloring logic
        value = parseFloat(rec.AvgTt) || parseFloat(rec.travel_time_in_seconds) || 0;
        hasData = value > 0;
      }
    } else {
      // Default to speed from metrics.json
      value = parseFloat(rec.AvgSp) || parseFloat(rec.speed) || 0;
      hasData = value > 0;
    }

    // When AI filter is active, show non-matching roads in gray
    if (aiFilteredRoads && aiFilteredRoads.length > 0) {
      if (!aiFilteredRoads.includes(featureHash)) {
        return {
          color: '#999999',
          weight: 2,
          opacity: 0.4,
          lineCap: 'round',
        };
      }
    }

    // Determine color: blue if highlighted, purple if selected, darker cyan if no data, otherwise use color scale
    let color;
    if (isHighlighted) {
      color = "#3B82F6"; // Clear Blue
    } else if (isSel) {
      color = "#8C74FF"; // Primary Selection Violet
    } else if (!hasData || allRecords.length === 0) {
      color = "#4A5568"; // Neutral gray for no data
    } else {
      color = colorScale(value).hex();
    }

    return {
      color: color,
      weight: isSel || isHighlighted ? 8 : 4,
      opacity: isSel || isHighlighted ? 1 : 0.8,
      lineCap: "round",
    };
  };

  const offsetSegmentiNegativi = (data, distanza = 4) => {
    const offsetFeatures = data.features.map((f) => {
      const segId = f.properties["Segment Id"];
      if (String(segId).startsWith("-")) {
        try {
          const offsetLine = turf.lineOffset(f, distanza, { units: "meters" });
          return { ...f, geometry: offsetLine.geometry };
        } catch (e) {
          console.warn("Offset fallito per segmento", segId);
          return f;
        }
      }
      return f;
    });
    return { ...data, features: offsetFeatures };
  };

  const onFeatureSelect = (hash, featureProperties) => {
    setSelectedHash((prevHash) => {
      // Normalize to array
      const currentRoads = Array.isArray(prevHash)
        ? prevHash
        : prevHash
          ? [prevHash]
          : [];

      // Check if this road is already selected
      const isAlreadySelected = currentRoads.includes(hash);

      if (isAlreadySelected) {
        // DESELECT: Remove this road
        const newHash = currentRoads.filter((h) => h !== hash);

        setSelectedFeature((prevFeatures) => {
          const currentFeatures = Array.isArray(prevFeatures)
            ? prevFeatures
            : prevFeatures
              ? [prevFeatures]
              : [];
          return currentFeatures.filter((f) => f.hash !== hash);
        });

        // If we're deselecting and no roads remain, close the sider
        if (newHash.length === 0 && !radarSelected) {
          setSiderSection(null);
        }

        return newHash;
      } else {
        // SELECT: Add this road (enforce 2-element total limit)
        const radarCount = radarSelected ? 1 : 0;
        const totalSelected = currentRoads.length + radarCount;

        // Close network view card when selecting a road
        if (currentRoads.length === 0 && networkViewCategory) {
          // Opening from network view - keep the same section but it will show road data
          // The section stays the same (velocita, congestion, anomaly) but shows road-specific data
        }

        if (totalSelected >= 2) {
          // At capacity (2 elements selected)
          // FIFO: Remove oldest element to make room

          if (currentRoads.length === 2) {
            // Have 2 roads → remove oldest road
            const newHash = [currentRoads[1], hash];
            setSelectedFeature((prevFeatures) => {
              const currentFeatures = Array.isArray(prevFeatures)
                ? prevFeatures
                : prevFeatures
                  ? [prevFeatures]
                  : [];
              return [currentFeatures[1], featureProperties];
            });
            return newHash;
          } else if (currentRoads.length === 1 && radarSelected) {
            // Have 1 road + 1 radar → remove radar to add 2nd road
            setRadarSelected(false);
            const newHash = [...currentRoads, hash];
            setSelectedFeature((prevFeatures) => {
              const currentFeatures = Array.isArray(prevFeatures)
                ? prevFeatures
                : prevFeatures
                  ? [prevFeatures]
                  : [];
              return [...currentFeatures, featureProperties];
            });
            return newHash;
          }
        }

        // Under capacity → just add it
        const newHash = [...currentRoads, hash];
        setSelectedFeature((prevFeatures) => {
          const currentFeatures = Array.isArray(prevFeatures)
            ? prevFeatures
            : prevFeatures
              ? [prevFeatures]
              : [];
          return [...currentFeatures, featureProperties];
        });
        return newHash;
      }
    });

    // Update dates based on the clicked hash - always select LAST date
    const dates = [
      ...new Set(
        dbData
          ?.filter((d) => d.hash === hash && d.date)
          .map((d) => d.date.split("T")[0])
      ),
    ].sort();

    // Always select the FIRST available date
    if (dates.length > 0) {
      setSelectedDate(dates[0]);
    } else {
      setSelectedDate(null);
    }
  };

  const onRadarSelect = () => {
    setRadarSelected((prev) => {
      const willBeSelected = !prev;

      if (willBeSelected) {
        // SELECTING radar - enforce 2-element limit
        const currentRoads = Array.isArray(selectedHash)
          ? selectedHash
          : selectedHash
            ? [selectedHash]
            : [];
        const totalSelected = currentRoads.length; // radar not counted yet

        if (totalSelected >= 2) {
          // Have 2 roads → remove oldest road to make room for radar
          const newHash = currentRoads.slice(1);
          setSelectedHash(newHash);
          setSelectedFeature((prevFeatures) => {
            const currentFeatures = Array.isArray(prevFeatures)
              ? prevFeatures
              : prevFeatures
                ? [prevFeatures]
                : [];
            return currentFeatures.slice(1);
          });
        }
        // else: Have 0 or 1 roads → just add radar (total will be 1 or 2)
      }
      // DESELECTING radar → just remove it (don't touch roads)

      return willBeSelected;
    });
  };

  // const arco = dbData?.find((d) => d.hash === selectedHash);

  // Extract available dates from the traffic data
  const availableDates = useMemo(() => {
    if (!dbData || !selectedHash || selectedHash.length === 0) return [];

    const dates = new Set();
    dbData
      .filter((d) => selectedHash.includes(d.UnifiedID) || selectedHash.includes(d.hash))
      .forEach((d) => {
        // Priority 1: t_start field (format: "2024-08-01 00:00:00")
        if (d.t_start && typeof d.t_start === 'string' && d.t_start.trim() !== '') {
          const datePart = d.t_start.split(" ")[0];
          if (datePart.match(/^\d{4}-\d{2}-\d{2}$/)) {
            dates.add(datePart);
            return;
          }
        }
        // Priority 2: day_type + hour fields (saronno format)
        if (d.day_type && d.hour !== undefined && d.hour !== null && d.hour !== '') {
          dates.add(d.day_type);
          return;
        }
        // Priority 3: timestamp_utc (format: "2025-05-10 07:50:23+00:00")
        if (d.timestamp_utc) {
          const date = d.timestamp_utc.split(" ")[0];
          dates.add(date);
        }
        // Priority 4: date field (format: "2024-11-22T00:00:00.000")
        else if (d.date) {
          const date = d.date.split("T")[0];
          dates.add(date);
        }
      });

    return Array.from(dates).sort();
  }, [dbData, selectedHash]);

  // Show all roads (AI filter now handles styling in styleFeature, not filtering)
  const filteredGeoData = geoData;

  /*
  const selectedData = dbData
    ?.filter((d) => d.hash === selectedHash && d.date?.startsWith(selectedDate))
    ?.sort((a, b) => a.timestamp - b.timestamp)
    ?.map((d) => {
      const date = new Date(d.timestamp * 1000);
      const hh = String(date.getHours()).padStart(2, "0");
      const mm = String(date.getMinutes()).padStart(2, "0");
      const ss = String(date.getSeconds()).padStart(2, "0");
      const orario = `${hh}:${mm}:${ss}`;

      const velocita =
        d.travel_time_in_seconds > 0
          ? (d.length_in_meters / d.travel_time_in_seconds) * 3.6
          : 0;

      return {
        time: orario,
        velocità: Math.round(velocita * 10) / 10,
        tempo_percorrenza: d.travel_time_in_seconds,
        tempo_storico: d.historic_traffic_travel_time_in_seconds,
        lunghezza_coda: d.traffic_length_in_meters,
      };
    });
  */

  // Handler to highlight roads from AI analysis results
  const handleAIHighlightRoads = (unifiedIds) => {
    if (!unifiedIds || unifiedIds.length === 0) {
      // Clear selection and filter when empty
      setSelectedHash([]);
      setSelectedFeature([]);
      setAiFilteredRoads(null);
      return;
    }

    // Set the selected hashes to highlight them on the map
    setSelectedHash(unifiedIds);

    // Set AI filter to only show these roads on the map
    setAiFilteredRoads(unifiedIds);

    // Find and set the selected features from geoData
    if (geoData && geoData.features) {
      const matchingFeatures = geoData.features
        .filter(f => {
          const hash = f.properties.hash || f.properties.UnifiedID;
          return unifiedIds.includes(hash);
        })
        .map(f => f.properties);

      setSelectedFeature(matchingFeatures);
    }
  };

  const getPopupWidthClass = () => {
    if (siderSection === "video") return "width-video";
    if (siderSection === "proprieta") return "width-proprieta";
    if (siderSection === "ai") return "width-ai";
    // Network view cards are smaller
    if (
      siderSection === "velocita" &&
      (!selectedHash || selectedHash.length === 0)
    )
      return "width-default";
    if (
      siderSection === "flow" &&
      (!selectedHash || selectedHash.length === 0)
    )
      return "width-default";
    if (
      siderSection === "traveltime" &&
      (!selectedHash || selectedHash.length === 0)
    )
      return "width-default";
    if (siderSection === "velocita") return "width-proprieta";
    if (siderSection === "flow") return "width-proprieta";
    if (siderSection === "traveltime") return "width-proprieta";
    if (siderSection === "ambientali") return "width-ambientali";
    if (siderSection === "accelerometrici") return "width-accelerometrici";
    if (siderSection === "audio") return "width-audio";
    if (siderSection === "confronto") return "width-proprieta";
    return "width-default";
  };
  const [screenSaverOn, setScreenSaverOn] = useState(false)
  return (
    <Layout
      className={`geojson-layout ${isMobile ? "geojson-layout-mobile" : "geojson-layout-desktop"
        }`}
    >
      {/**
       * Sidebar commented out for full-screen map
       * <SiderComponent
       *   isMobile={isMobile}
       *   collapsed={collapsed}
       *   setCollapsed={setCollapsed}
       *   siderSection={siderSection}
       *   selectedFeature={selectedFeature}
       *   arco={arco}
       *   availableDates={availableDates}
       *   selectedDate={selectedDate}
       *   setSelectedDate={setSelectedDate}
       *   selectedData={selectedData}
       * />
       */}
      {(screenSaverOn && localStorage.getItem("client_type").toLowerCase() == "lavagna") && <ScreensaverOverlay onClick={() => {
        setScreenSaverOn(prv => !prv)
      }} />}
      <Layout className="geojson-inner-layout">
        <Content className="geojson-content">
          {/*
            Logo rules:
            - FCD NETX present → previous Traffix / client_type logo
            - No FCD NETX (mystery-client map) → company logo from clients.json
          */}
          {hasFcdNetwork ? (
            <Tooltip
              title={(localStorage.getItem("client_type") || "Client")
                .replace(/_/g, " ")
                .toUpperCase()}
              placement="bottomRight"
            >
              <div
                className="traffix-logo-container"
                onClick={() => setScreenSaverOn(prv => !prv)}
              >
                <img src={clientLogo} alt="Logo" className="betwin-logo-img" />
              </div>
            </Tooltip>
          ) : (
            <McClientLogo onClick={() => setScreenSaverOn(prv => !prv)} />
          )}

          <div className="geojson-map-container">
            <MapComponent
              style={{
                padding: 0,
                borderRadius: 0,
                height: "100%",
                // keep part of the handle tappable even when closed
                paddingBottom: footerOpen ? FOOTER_HEIGHT : 0,
                overflow: "hidden",
                position: "relative",
              }}
              geoData={filteredGeoData}
              onFeatureSelect={onFeatureSelect}
              styleFeature={styleFeature}
              speedDomain={speedDomain}
              siderSection={siderSection}
              setSiderSection={setSiderSection}
              showDeckGL={showDeckGL}
              setShowDeckGL={setShowDeckGL}
              mapMode={mapMode}
              setMapMode={setMapMode}
              colorScale={colorScale}
              availableDates={availableDates}
              selectedDate={selectedDate}
              setSelectedDate={setSelectedDate}
              selectedHash={selectedHash}
              setSelectedHash={setSelectedHash}
              setSelectedFeature={setSelectedFeature}
              onRadarSelect={onRadarSelect}
              radarSelected={radarSelected}
              setRadarSelected={setRadarSelected}
              footerOpen={footerOpen}
              footerHeight={FOOTER_HEIGHT}
              isLoadingGeoData={isLoadingGeoData}
              networkViewCategory={networkViewCategory}
              setNetworkViewCategory={setNetworkViewCategory}
              networkViewDate={networkViewDate}
              setNetworkViewDate={setNetworkViewDate}
              networkViewTime={networkViewTime}
              setNetworkViewTime={setNetworkViewTime}
              availableNetworkDates={availableNetworkDates}
              availableNetworkTimes={availableNetworkTimes}
              onClearAIFilter={() => setAiFilteredRoads(null)}
              aiFilteredRoads={aiFilteredRoads}
              onMapCentered={handleMapCentered}
              mcCityData={mcCityData}
              hasMysteryClient={hasMysteryClient}
              hasOnlyMysteryClient={hasOnlyMysteryClient}
              shouldCenterOnMcCity={shouldCenterOnMcCity}
            />

            {/* Removed duplicate DataLoadingScreen here. Global proxy intercepts data fetches automatically. */}

            {/** Popup card overlay with Sider content */}
            {mapMode === "mappa" && siderSection && (
              <div className={`geojson-popup-card ${getPopupWidthClass()}`}>
                <button
                  onClick={() => {
                    setAiFilteredRoads(null);
                    setSiderSection(null);
                  }}
                  className="geojson-popup-close-btn"
                  aria-label="Close"
                >
                  ✕
                </button>
                <SiderComponent
                  isMobile={true}
                  collapsed={false}
                  setCollapsed={() => { }}
                  siderSection={siderSection}
                  onZoomToRoad={handleZoomToRoad}
                  selectedFeature={selectedFeature}
                  networkViewDate={networkViewDate}
                  setNetworkViewDate={setNetworkViewDate}
                  networkViewTime={networkViewTime}
                  setNetworkViewTime={setNetworkViewTime}
                  availableNetworkDates={availableNetworkDates}
                  availableNetworkTimes={availableNetworkTimes}
                  networkViewCategory={networkViewCategory}
                  arco={
                    selectedHash && selectedHash.length > 0
                      ? (() => {
                        const filteredData =
                          dbData
                            ?.filter((d) => selectedHash.includes(d.hash) || selectedHash.includes(d.UnifiedID))
                            .map((d) => {
                              // Normalize data format for compatibility with child components
                              const normalizedData = { ...d };

                              // Ensure date field exists
                              if (d.timestamp_utc && !d.date) {
                                // New format: convert timestamp_utc to date
                                normalizedData.date =
                                  d.timestamp_utc.split(" ")[0] +
                                  "T00:00:00.000";
                              }

                              // Ensure timestamp field exists (Unix timestamp in seconds)
                              if (d.time && !d.timestamp) {
                                normalizedData.timestamp = parseInt(d.time);
                              }

                              // Parse numeric strings to numbers for consistency
                              if (typeof d.length_in_meters === "string") {
                                normalizedData.length_in_meters = parseFloat(
                                  d.length_in_meters
                                );
                              }
                              if (
                                typeof d.travel_time_in_seconds === "string"
                              ) {
                                normalizedData.travel_time_in_seconds =
                                  parseFloat(d.travel_time_in_seconds);
                              }
                              if (
                                typeof d.historic_traffic_travel_time_in_seconds ===
                                "string"
                              ) {
                                normalizedData.historic_traffic_travel_time_in_seconds =
                                  parseFloat(
                                    d.historic_traffic_travel_time_in_seconds
                                  );
                              }
                              if (
                                typeof d.traffic_length_in_meters === "string"
                              ) {
                                normalizedData.traffic_length_in_meters =
                                  parseFloat(d.traffic_length_in_meters);
                              }
                              if (
                                typeof d.live_traffic_incidents_travel_time_in_seconds ===
                                "string"
                              ) {
                                normalizedData.live_traffic_incidents_travel_time_in_seconds =
                                  parseFloat(
                                    d.live_traffic_incidents_travel_time_in_seconds
                                  );
                              }
                              if (
                                typeof d.traffic_delay_in_seconds === "string"
                              ) {
                                normalizedData.traffic_delay_in_seconds =
                                  parseFloat(d.traffic_delay_in_seconds);
                              }

                              return normalizedData;
                            }) || [];

                        // Check which selected roads are missing from filteredData
                        // and create minimal objects for them so components know they ARE selected
                        if (
                          selectedHash &&
                          selectedHash.length > 0 &&
                          selectedFeature &&
                          selectedFeature.length > 0
                        ) {
                          // Get hashes that exist in filteredData
                          const hashesWithData = new Set(
                            filteredData.map((d) => d.hash)
                          );

                          // Find roads that are selected but have no data
                          const roadsWithoutData = selectedFeature.filter(
                            (feature) => !hashesWithData.has(feature.hash)
                          );

                          // Create minimal objects for roads without data
                          const minimalObjects = roadsWithoutData.map(
                            (feature) => ({
                              hash: feature.hash,
                              // Include basic properties from GeoJSON
                              ...(feature.StreetName && {
                                StreetName: feature.StreetName,
                              }),
                              ...(feature.Length && {
                                length_in_meters: feature.Length,
                              }),
                              ...(feature.SpeedLimit && {
                                speed_limit: feature.SpeedLimit,
                              }),
                            })
                          );

                          // Combine data: roads with data + roads without data
                          return [...filteredData, ...minimalObjects];
                        }

                        return filteredData;
                      })()
                      : [] // Empty array when no road is selected
                  }
                  geoData={geoData}
                  availableDates={availableDates}
                  selectedDate={selectedDate}
                  setSelectedDate={setSelectedDate}
                  radarSelected={radarSelected}
                  onHighlightRoads={handleAIHighlightRoads}
                  onCloseSider={() => {
                    setSiderSection(null);
                    setAiFilteredRoads(null);
                  }}
                  selectedData={
                    selectedHash && selectedHash.length > 0
                      ? dbData
                        ?.filter((d) => {
                          if (!selectedHash.includes(d.hash)) return false;
                          if (!selectedDate) return false;

                          // Support both date formats
                          if (d.timestamp_utc) {
                            return d.timestamp_utc.startsWith(selectedDate);
                          } else if (d.date) {
                            return d.date.startsWith(selectedDate);
                          }
                          return false;
                        })
                        ?.sort((a, b) => {
                          const timeA = a.timestamp || parseInt(a.time) || 0;
                          const timeB = b.timestamp || parseInt(b.time) || 0;
                          return timeA - timeB;
                        })
                        ?.map((d) => {
                          const timestamp =
                            d.timestamp || parseInt(d.time) || 0;
                          const date = new Date(timestamp * 1000);
                          const hh = String(date.getHours()).padStart(2, "0");
                          const mm = String(date.getMinutes()).padStart(
                            2,
                            "0"
                          );
                          const ss = String(date.getSeconds()).padStart(
                            2,
                            "0"
                          );
                          const orario = `${hh}:${mm}:${ss}`;

                          let velocita = 0;
                          if (d.speed) {
                            // New format has speed directly
                            velocita = parseFloat(d.speed);
                          } else if (
                            d.travel_time_in_seconds &&
                            parseFloat(d.travel_time_in_seconds) > 0
                          ) {
                            // Old format: calculate speed
                            velocita =
                              (parseFloat(d.length_in_meters) /
                                parseFloat(d.travel_time_in_seconds)) *
                              3.6;
                          }

                          return {
                            time: orario,
                            velocità: Math.round(velocita * 10) / 10,
                            tempo_percorrenza:
                              parseFloat(d.travel_time_in_seconds) || 0,
                            tempo_storico:
                              parseFloat(
                                d.historic_traffic_travel_time_in_seconds
                              ) || 0,
                            lunghezza_coda:
                              parseFloat(d.traffic_length_in_meters) || 0,
                          };
                        })
                      : [] // Empty array when no road is selected
                  }
                />
              </div>
            )}



          </div>
        </Content>
      </Layout>
    </Layout>
  );
}

export default GeoJson;
